/**
 * RouteErrorBoundary — wraps the whole app; catches any uncaught render
 * error, ships a report to /api/platform-public/route-error, and renders
 * a graceful fallback UI so the visitor isn't left on a white screen.
 *
 * Deliberate: we still render a clear message + reload CTA even when the
 * reporter call fails, so a reporting outage never compounds a render bug.
 */
import React from "react";
import RouteHealthReporter from "../lib/routeHealthReporter";

export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    RouteHealthReporter.report({
      kind: "crash",
      message: (error && error.message) || String(error),
      stack: (info && info.componentStack) || (error && error.stack) || null,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-slate-50" data-testid="route-error-fallback">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xl mb-4">!</div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-slate-500 mb-6">
            The page couldn't load. Our team has been notified automatically. Please try again.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              Reload page
            </button>
            <button
              onClick={() => { window.location.href = "/"; }}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium hover:bg-white"
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
